import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { ItensEditor, linhasParaApi } from './ItensEditor';
import { invalidarCompras, novaLinha, type CotacaoDetalhe, type LinhaItem, type RequisicaoDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { nomeCadastro, nomePessoa } from '../../nucleo/formato';

/**
 * Drawer de nova cotação (M10.1) ou de edição. Os produtos vêm da requisição: o comprador só lança preço e prazo de
 * cada item. O id nasce com a tela, então reenviar nunca cria uma segunda cotação.
 */
export function FormCotacao({ aberto, requisicaoId, cotacao, aoFechar, aoSalvar }: { aberto: boolean; requisicaoId?: string | null; cotacao?: CotacaoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  if (!aberto) return null;
  return <Corpo requisicaoIdInicial={requisicaoId ?? cotacao?.requisicao_id ?? null} cotacao={cotacao ?? null} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ requisicaoIdInicial, cotacao, aoFechar, aoSalvar }: { requisicaoIdInicial: string | null; cotacao: CotacaoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const [novoId] = useState(() => crypto.randomUUID());
  const [requisicaoId, setRequisicaoId] = useState<string | null>(requisicaoIdInicial);
  const [fornecedorId, setFornecedorId] = useState<string | null>(cotacao?.fornecedor_id ?? null);
  const [validade, setValidade] = useState(cotacao?.validade ?? '');
  const [condicao, setCondicao] = useState(cotacao?.condicao_pagamento ?? '');
  const [observacao, setObservacao] = useState(cotacao?.observacao ?? '');
  const [linhas, setLinhas] = useState<LinhaItem[]>(
    cotacao
      ? cotacao.itens_detalhe.map((i) => ({
          ...novaLinha(),
          produto_id: i.produto_id,
          produto_rotulo: `${i.produto_codigo ?? ''} ${nomeCadastro(i.produto_nome)}`.trim(),
          quantidade: String(i.quantidade).replace('.', ','),
          preco: i.preco_unitario_centavos,
          prazo: i.prazo_dias === null ? '' : String(i.prazo_dias),
        }))
      : [],
  );
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  // Sem cotação existente, os itens saem da requisição escolhida.
  const requisicao = useQuery({
    queryKey: ['/compras/requisicoes', 'obter', requisicaoId],
    queryFn: () => get<RequisicaoDetalhe>(`/compras/requisicoes/${requisicaoId}`),
    enabled: !!requisicaoId && !cotacao,
  });
  useEffect(() => {
    if (!requisicao.data || cotacao) return;
    setLinhas(requisicao.data.itens_detalhe.map((i) => ({ ...novaLinha(), produto_id: i.produto_id, produto_rotulo: `${i.produto_codigo ?? ''} ${nomeCadastro(i.produto_nome)}`.trim(), quantidade: String(i.quantidade).replace('.', ',') })));
  }, [requisicao.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const enviar = useMutation({
    mutationFn: async () => {
      if (!requisicaoId) throw new Error(t('erros.escolhaRequisicao'));
      if (!fornecedorId) throw new Error(t('erros.escolhaFornecedor'));
      const itens = linhasParaApi(linhas, { preco: true, prazo: true }, { semItens: t('erros.semItens'), incompleto: t('erros.itemIncompleto') });
      const corpo = { fornecedor_id: fornecedorId, validade: validade || null, condicao_pagamento: condicao.trim() || null, observacao: observacao.trim() || null, itens };
      if (cotacao) {
        await patch(`/compras/cotacoes/${cotacao.id}`, corpo);
        return cotacao.id;
      }
      await post('/compras/cotacoes', { id: novoId, requisicao_id: requisicaoId, ...corpo });
      return novoId;
    },
    onSuccess: async (id) => {
      await invalidarCompras(qc);
      toast.sucesso(tn('comum.salvo'));
      aoSalvar?.(id);
      aoFechar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  const { ref: refForm } = useValidacaoLocal(erros, erro);

  function submeter(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    setErros({});
    enviar.mutate();
  }

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={cotacao ? t('cotacao.editarTitulo', { fornecedor: nomePessoa(cotacao.fornecedor_nome) }) : t('acoes.novaCotacao')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-cotacao" carregando={enviar.isPending}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={refForm} id="form-cotacao" onSubmit={submeter} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-6">
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
            {erro}
          </p>
        )}
        <div className="sm:col-span-6">
          {cotacao || requisicaoIdInicial ? (
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="text-sm font-medium text-tinta-2">{t('campos.requisicao')}</span>
              <TextoCortado className="text-sm text-tinta-1">{cotacao?.requisicao_numero ?? requisicao.data?.numero ?? '...'}</TextoCortado>
            </div>
          ) : (
            <CampoControlado
              def={{
                nome: 'requisicao_id',
                rotulo: t('campos.requisicao'),
                tipo: 'busca',
                obrigatorio: true,
                ajuda: t('ajuda.requisicaoCotacao'),
                busca: { caminho: '/compras/requisicoes', params: { estado: 'enviada,aprovada' }, rotulo: (i) => `${String(i.numero)} ${String(i.justificativa)}` },
              }}
              valor={requisicaoId}
              aoMudar={(v) => setRequisicaoId((v as string | null) || null)}
              erro={erros.requisicao_id}
            />
          )}
        </div>
        <div className="sm:col-span-6">
          <CampoControlado
            def={{ nome: 'fornecedor_id', rotulo: t('campos.fornecedor'), tipo: 'busca', obrigatorio: true, busca: { caminho: '/cadastros/parceiros', params: { papel: 'fornecedor' }, rotulo: (i) => String(i.nome) } }}
            valor={fornecedorId}
            rotuloAtual={nomePessoa(cotacao?.fornecedor_nome)}
            aoMudar={(v) => setFornecedorId((v as string | null) || null)}
            erro={erros.fornecedor_id}
          />
        </div>
        <div className="sm:col-span-2">
          <CampoControlado def={{ nome: 'validade', rotulo: t('campos.validade'), tipo: 'data', ajuda: t('ajuda.validade') }} valor={validade} aoMudar={(v) => setValidade(String(v ?? ''))} erro={erros.validade} />
        </div>
        <div className="sm:col-span-2">
          <CampoControlado def={{ nome: 'condicao_pagamento', rotulo: t('campos.condicao'), tipo: 'texto', placeholder: '30/60/90', ajuda: t('ajuda.condicao') }} valor={condicao} aoMudar={(v) => setCondicao(String(v ?? ''))} erro={erros.condicao_pagamento} />
        </div>
        <div className="sm:col-span-2">
          <CampoControlado def={{ nome: 'observacao', rotulo: t('campos.observacao'), tipo: 'texto' }} valor={observacao} aoMudar={(v) => setObservacao(String(v ?? ''))} erro={erros.observacao} />
        </div>
        {requisicaoId && linhas.length === 0 && !requisicao.isError && <p className="text-sm text-tinta-3 sm:col-span-6">{tn('comum.carregando')}</p>}
        {linhas.length > 0 && <ItensEditor linhas={linhas} aoMudar={setLinhas} comPreco comPrazo produtosFixos moeda={s.workspace.moeda} />}
      </form>
    </Drawer>
  );
}
